import { useState, useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useTranslation } from "react-i18next";
import { Navbar } from "@/shared/components/layout/Navbar";
import { Sidebar } from "@/shared/components/layout/Sidebar";
import { WORKFLOWS_PATH, workflowSettingsPath } from "@/features/Agents/routes";
import { WorkspaceGuard } from "@/shared/components/layout/WorkspaceGuard";
import { SuperAdminGuard } from "@/shared/components/layout/SuperAdminGuard";
import { LoadingSpinner } from "@/shared/components/ui/loading-spinner";
import { useLanguageLoader } from "@/shared/hooks/use-language-loader";
import { useSidebarState } from "@/shared/hooks/use-sidebar-state";
import { useWorkspace } from "@/features/Workspaces/state/WorkspaceContext";
import Login from "@/features/Auth/components/Login";
import ProfileView from "@/features/Profile/components/ProfileView";
import SettingsView from "@/features/Settings/components/SettingsView";
import IntegrationsBase from "@/features/Integrations/components/IntegrationsBase";
import Knowledge from "@/features/Knowledge/components/Knowledge";
import PlaygroundView from "@/features/Playground/components/PlaygroundView";
import AgentBase from "@/features/Agents/components/AgentBase";
import SkillsPage from "@/features/Skills/components/SkillsPage";
import MemoryPage from "@/features/Memory/components/MemoryPage";
import JobsPage from "@/features/Jobs/components/JobsPage";
import SkillSettings from "@/features/Skills/components/SkillSettings";
import AgentSettings from "@/features/Agents/components/AgentSettings";
import { DashboardView } from "@/features/Dashboards/components/DashboardView";
import { LogViewer } from "@/features/LogMonitor/components/LogViewer";
import { FeedbackSettings } from "@/features/Settings/components/FeedbackSettings";
import { PlansSettings } from "@/features/Settings/components/PlansSettings";
import { DashboardTemplatesSettings } from "@/features/Settings/components/DashboardTemplatesSettings";
import { SkillAdminDetail } from "@/features/Settings/components/SkillAdminDetail";
import { TemplatesSettings } from "@/features/Settings/components/TemplatesSettings";
import { PlatformSettings } from "@/features/PlatformSettings/components/PlatformSettings";
import { EmailSettingsView } from "@/features/Settings/components/EmailSettingsView";
import { AssistantSettings } from "@/features/Assistant/components/AssistantSettings";
import AccessDenied from "@/shared/components/error/AccessDenied";
import SetupPassword from "@/features/Auth/components/SetupPassword";
import ResetPassword from "@/features/Auth/components/ResetPassword";
import RecoverPassword from "@/features/Auth/components/RecoverPassword";
import SignUp from "@/features/Auth/components/SignUp";
import Onboarding from "@/features/Onboarding/components/OnboardingContainer";
import Unsubscribe from "@/features/Auth/components/Unsubscribe";
import OAuthCallback from "@/features/Auth/components/OAuthCallback";
import OAuthConsent from "@/features/Auth/components/OAuthConsent";
import OAuthError from "@/features/Auth/components/OAuthError";
import NotFound from "@/shared/components/error/NotFound";
import { useFeedbackPrompt } from "@/features/Feedback/hooks/useFeedbackPrompt";
import { FeedbackModal } from "@/features/Feedback/components/FeedbackModal";
import { WorkspaceConfigPage } from "@/features/Workspaces/pages/WorkspaceConfigPage";
import CommunicatorLogin from "@/features/Communicator/components/CommunicatorLogin";
import WidgetChat from "@/features/Widget/components/WidgetChat";
import NoOrganizationScreen from "@/features/Auth/components/NoOrganizationScreen";

interface PrivateRouteProps {
  children: React.ReactNode;
}

/** `/agents/:id/settings` → `/workflows/:id/settings` (AB#86605), sem perder o id. */
function LegacyWorkflowSettingsRedirect() {
  const { id } = useParams();
  return <Navigate to={id ? workflowSettingsPath(id) : WORKFLOWS_PATH} replace />;
}

function PrivateRoute({ children }: PrivateRouteProps) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return children;
}

export function AppRoutes() {
  const sidebar = useSidebarState();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isSwitchingProduct, setIsSwitchingProduct] = useState(false);
  const { user, loading, isAuthenticating } = useAuth();
  const {
    isLoading: isLoadingProducts,
    isReloading,
    productChangeKey,
  } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  useLanguageLoader();

  const isSettingsPage = location.pathname === "/settings";

  // Feedback prompt baseado na sessão do usuário autenticado
  const feedback = useFeedbackPrompt({
    isAuthenticated: !!user,
    userId: user?.id,
  });

  // Handle redirect after authentication
  useEffect(() => {
    if (user && !loading && !isAuthenticating) {
      const redirectUrl = sessionStorage.getItem("prodgy_redirect_after_login");
      const currentPath = window.location.pathname;

      // Only redirect if we're on login page and have a saved URL
      if (currentPath === "/login" && redirectUrl) {
        setIsRedirecting(true);
        sessionStorage.removeItem("prodgy_redirect_after_login");

        // Small delay to prevent flashing
        setTimeout(() => {
          navigate(redirectUrl, { replace: true });
          setIsRedirecting(false);
        }, 100);
      }
    }
  }, [user, loading, isAuthenticating, navigate]);

  useEffect(() => {
    if (!productChangeKey) {
      return;
    }

    setIsSwitchingProduct(true);

    const timeoutId = setTimeout(() => {
      setIsSwitchingProduct(false);
    }, 800);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [productChangeKey]);

  // Show loading during auth check, redirect, or products loading
  // Aguarda tanto autenticação quanto produtos estarem prontos antes de renderizar
  if (
    loading ||
    isRedirecting ||
    isAuthenticating ||
    (user && isLoadingProducts)
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const publicRoutes = (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/auth/setup-password" element={<SetupPassword />} />
      <Route path="/auth/reset-password" element={<ResetPassword />} />
      <Route path="/oauth/callback" element={<OAuthCallback />} />
      <Route path="/oauth/consent" element={<OAuthConsent />} />
      <Route path="/auth/error" element={<OAuthError />} />
      <Route path="/recover-password" element={<RecoverPassword />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      {/* Standalone, mas NAO publica: sem sessao, redireciona pro login antes de
          renderizar qualquer conteudo do onboarding (bug 82809). */}
      <Route
        path="/onboarding"
        element={
          <PrivateRoute>
            <Onboarding />
          </PrivateRoute>
        }
      />
      <Route path="*" element={<Navigate to="/login" />} />
    </Routes>
  );

  // Standalone routes — render without Navbar/Sidebar layout
  const currentPath = window.location.pathname;

  // Widget chat is fully public — no auth, no layout
  if (currentPath.startsWith("/widget/")) {
    return (
      <Routes>
        <Route path="/widget/chat/:workspaceId" element={<WidgetChat />} />
      </Routes>
    );
  }

  // Communicator login handles auth internally (redirects to /login if needed)
  if (currentPath.startsWith("/communicator/")) {
    return (
      <Routes>
        <Route path="/communicator/login" element={<CommunicatorLogin />} />
      </Routes>
    );
  }

  // Se a URL atual é uma das rotas públicas de autenticação, sempre mostrar as rotas públicas
  if (
    currentPath === "/auth/setup-password" ||
    currentPath === "/auth/reset-password" ||
    currentPath === "/oauth/callback" ||
    currentPath === "/oauth/consent" ||
    currentPath === "/auth/error" ||
    currentPath === "/recover-password" ||
    currentPath === "/signup" ||
    currentPath === "/unsubscribe" ||
    currentPath === "/onboarding"
  ) {
    return publicRoutes;
  }

  // Se o usuário não está autenticado, mostrar rotas públicas
  if (!user) {
    return publicRoutes;
  }

  // Usuário sem organização (org deletada ou perfil órfão): estado restrito com a
  // navbar padrão (modo restricted), acesso só ao próprio perfil — evita a cascata
  // de 403/404 de queries dependentes de org. Superadmin opera sem org (cross-org)
  // e onboarding pendente já é redirecionado pro /onboarding pelo AuthContext.
  // Depende do organization_id fresco do /auth/me (a claim do JWT fica stale).
  const onboardingPending =
    user.onboardingStatus != null && user.onboardingStatus !== "completed";
  if (!user.isSuperAdmin && !user.organizationId && !onboardingPending) {
    return (
      <>
        <Navbar onMenuClick={() => {}} restricted />
        <Routes>
          <Route
            path="/profile"
            element={
              <main className="pt-16 px-4 lg:px-8">
                <div className="container py-6 mx-auto">
                  <ProfileView />
                </div>
              </main>
            }
          />
          <Route path="*" element={<NoOrganizationScreen />} />
        </Routes>
      </>
    );
  }

  // Usuário autenticado, mostrar a aplicação
  return (
    <div className="min-h-screen">
      {/* Loading overlay após salvar produto */}
      {(isReloading || isSwitchingProduct) && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <LoadingSpinner size="lg" />
            <p className="text-sm text-muted-foreground">
              {t("common.loading")}
            </p>
          </div>
        </div>
      )}

      <Navbar onMenuClick={sidebar.toggle} />
      <Sidebar
        isOpen={sidebar.mobileOpen}
        collapsed={sidebar.collapsed}
        expanded={sidebar.expanded}
        onClose={sidebar.closeMobile}
        onExpand={sidebar.toggle}
      />
      <main
        className={`
          pt-16 
          transition-all 
          duration-300
          ${isSettingsPage ? "" : "px-4 lg:px-8"}
          ${sidebar.collapsed ? "pl-0 lg:pl-16" : "pl-0 lg:pl-64"}
        `}
      >
        <div className={isSettingsPage ? "py-6" : "container py-6 mx-auto"}>
          <Routes>
            <Route
              path="/"
              element={
                <PrivateRoute>
                  {isLoadingProducts ? (
                    <LoadingSpinner />
                  ) : (
                    <WorkspaceGuard
                      allowedScopes={["platform", "organization", "product"]}
                    >
                      <DashboardView />
                    </WorkspaceGuard>
                  )}
                </PrivateRoute>
              }
            />
            <Route
              path="/integrations"
              element={
                <PrivateRoute>
                  <WorkspaceGuard allowedScopes={["organization", "product"]}>
                    <IntegrationsBase />
                  </WorkspaceGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/playground"
              element={
                <PrivateRoute>
                  <WorkspaceGuard>
                    <PlaygroundView />
                  </WorkspaceGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <ProfileView />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <SettingsView sidebarOpen={sidebar.isDesktop && !sidebar.collapsed} />
                </PrivateRoute>
              }
            />
            <Route
              path="/products/:workspaceId/config"
              element={
                <PrivateRoute>
                  <WorkspaceGuard>
                    <WorkspaceConfigPage />
                  </WorkspaceGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/knowledge"
              element={
                <PrivateRoute>
                  <WorkspaceGuard>
                    <Knowledge />
                  </WorkspaceGuard>
                </PrivateRoute>
              }
            />
            {/* Workflows (AB#86605): a área antes chamada Agents. As rotas antigas
                redirecionam, para links salvos não quebrarem. */}
            <Route
              path="/workflows"
              element={
                <PrivateRoute>
                  <WorkspaceGuard>
                    <AgentBase />
                  </WorkspaceGuard>
                </PrivateRoute>
              }
            />
            <Route path="/agents" element={<Navigate to="/workflows" replace />} />
            {/* Jobs (AB#86605, AC13): uma Skill num horário ou webhook, em lista. */}
            <Route
              path="/jobs"
              element={
                <PrivateRoute>
                  <WorkspaceGuard>
                    <JobsPage />
                  </WorkspaceGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/skills"
              element={
                <PrivateRoute>
                  {/* Os TRÊS níveis (AB#85634, AC19; AB#85637): a mesma tela
                      serve organização, workspace e espaço pessoal, e é
                      `useViewScope` que diz qual está ativo. */}
                  <WorkspaceGuard allowedScopes={["organization", "product", "user"]}>
                    <SkillsPage />
                  </WorkspaceGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/memory"
              element={
                <PrivateRoute>
                  {/* Memory da organização e pessoal (AB#87149). A do workspace é uma
                      aba do Brain; o nível sai de `useViewScope`, como nas Skills. */}
                  <WorkspaceGuard allowedScopes={["organization", "user"]}>
                    <MemoryPage />
                  </WorkspaceGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/skills/:name/settings"
              element={
                <PrivateRoute>
                  <WorkspaceGuard allowedScopes={["organization", "product", "user"]}>
                    <SkillSettings />
                  </WorkspaceGuard>
                </PrivateRoute>
              }
            />
            {/* Criar é pela instalação na lista ou pela IA (tools workflows_*);
                não há tela própria — o antigo placeholder "New Agent" saiu. */}
            <Route path="/workflows/new" element={<Navigate to="/workflows" replace />} />
            <Route path="/agents/new" element={<Navigate to="/workflows" replace />} />
            <Route
              path="/workflows/:id/settings"
              element={
                <PrivateRoute>
                  <WorkspaceGuard>
                    <AgentSettings />
                  </WorkspaceGuard>
                </PrivateRoute>
              }
            />
            <Route path="/agents/:id/settings" element={<LegacyWorkflowSettingsRedirect />} />
            <Route
              path="/admin/platform"
              element={
                <PrivateRoute>
                  <SuperAdminGuard>
                    <PlatformSettings />
                  </SuperAdminGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/queues"
              element={
                <PrivateRoute>
                  <SuperAdminGuard>
                    <AssistantSettings />
                  </SuperAdminGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/logs"
              element={
                <PrivateRoute>
                  <SuperAdminGuard>
                    <LogViewer />
                  </SuperAdminGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/feedback"
              element={
                <PrivateRoute>
                  <SuperAdminGuard>
                    <FeedbackSettings />
                  </SuperAdminGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/email"
              element={
                <PrivateRoute>
                  <SuperAdminGuard>
                    <EmailSettingsView />
                  </SuperAdminGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/plans"
              element={
                <PrivateRoute>
                  <SuperAdminGuard>
                    <PlansSettings />
                  </SuperAdminGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/templates"
              element={
                <PrivateRoute>
                  <SuperAdminGuard>
                    <TemplatesSettings />
                  </SuperAdminGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/skills/:name"
              element={
                <PrivateRoute>
                  <SuperAdminGuard>
                    <SkillAdminDetail />
                  </SuperAdminGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/dashboard-templates"
              element={
                <PrivateRoute>
                  <SuperAdminGuard>
                    <DashboardTemplatesSettings />
                  </SuperAdminGuard>
                </PrivateRoute>
              }
            />
            <Route
              path="/access-denied"
              element={
                <PrivateRoute>
                  <AccessDenied />
                </PrivateRoute>
              }
            />
            <Route path="/auth/setup-password" element={<SetupPassword />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            <Route path="/recover-password" element={<RecoverPassword />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/onboarding" element={<Onboarding />} />
            {/* Rota catch-all para usuários autenticados - mostra 404 ao invés de redirecionar */}
            <Route
              path="*"
              element={
                <PrivateRoute>
                  <NotFound />
                </PrivateRoute>
              }
            />
          </Routes>
        </div>
      </main>
      <FeedbackModal
        open={feedback.isOpen}
        config={feedback.config}
        loading={feedback.isLoading}
        onClose={feedback.close}
        onSubmit={feedback.submit}
      />
    </div>
  );
}
